import { useEffect, useCallback, useState, createContext, useContext, useRef } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    counterRef.current += 1;
    const id = `toast-${Date.now()}-${counterRef.current}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearAll }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }

  const { addToast, removeToast, clearAll } = ctx;

  const success = useCallback(
    (title: string, message?: string, duration = 4000) =>
      addToast({ type: 'success', title, message, duration }),
    [addToast]
  );

  const error = useCallback(
    (title: string, message?: string, duration = 6000) =>
      addToast({ type: 'error', title, message, duration }),
    [addToast]
  );

  const warning = useCallback(
    (title: string, message?: string, duration = 5000) =>
      addToast({ type: 'warning', title, message, duration }),
    [addToast]
  );

  const info = useCallback(
    (title: string, message?: string, duration = 4000) =>
      addToast({ type: 'info', title, message, duration }),
    [addToast]
  );

  return { success, error, warning, info, remove: removeToast, clearAll };
}

// ─── Individual Toast Item ────────────────────────────────────────────────────

const TOAST_ICONS: Record<ToastType, string> = {
  success: 'ri-checkbox-circle-line',
  error: 'ri-error-warning-line',
  warning: 'ri-alert-line',
  info: 'ri-information-line',
};

const TOAST_STYLES: Record<ToastType, { bar: string; icon: string; bg: string }> = {
  success: {
    bar: 'bg-emerald-500',
    icon: 'text-emerald-500',
    bg: 'bg-white border-emerald-100',
  },
  error: {
    bar: 'bg-red-500',
    icon: 'text-red-500',
    bg: 'bg-white border-red-100',
  },
  warning: {
    bar: 'bg-amber-400',
    icon: 'text-amber-500',
    bg: 'bg-white border-amber-100',
  },
  info: {
    bar: 'bg-sky-500',
    icon: 'text-sky-500',
    bg: 'bg-white border-sky-100',
  },
};

function ToastItem({
  toast,
  onRemove,
}: {
  toast: Toast;
  onRemove: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const duration = toast.duration ?? 4000;
  const styles = TOAST_STYLES[toast.type];

  // Axis 2 fix: success/info are non-urgent → polite; error/warning are urgent → assertive
  const isUrgent = toast.type === 'error' || toast.type === 'warning';

  // Animate in
  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), 10);
    return () => window.clearTimeout(t);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    if (duration <= 0) return;
    const t = window.setTimeout(() => {
      setLeaving(true);
      window.setTimeout(() => onRemove(toast.id), 300);
    }, duration);
    return () => window.clearTimeout(t);
  }, [duration, toast.id, onRemove]);

  const handleClose = () => {
    setLeaving(true);
    window.setTimeout(() => onRemove(toast.id), 300);
  };

  return (
    <div
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={[
        'relative flex items-start gap-3 w-full max-w-sm rounded-xl border shadow-lg px-4 py-3 overflow-hidden',
        'transition-all duration-300 ease-out',
        styles.bg,
        visible && !leaving
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 translate-x-8',
      ].join(' ')}
    >
      {/* Colored left bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${styles.bar}`} />

      {/* Icon */}
      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${styles.icon}`}>
        <i className={`${TOAST_ICONS[toast.type]} text-lg`} aria-hidden="true" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-snug">{toast.title}</p>
        {toast.message && (
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{toast.message}</p>
        )}
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={handleClose}
        aria-label="Dismiss notification"
        className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer rounded"
      >
        <i className="ri-close-line text-base" aria-hidden="true" />
      </button>

      {/* Progress bar */}
      {duration > 0 && (
        <div
          className={`absolute bottom-0 left-0 h-0.5 ${styles.bar} opacity-30`}
          style={{
            width: '100%',
            animation: `toast-shrink ${duration}ms linear forwards`,
          }}
        />
      )}
    </div>
  );
}

// ─── Container ────────────────────────────────────────────────────────────────

function ToastContainer({
  toasts,
  onRemove,
}: {
  toasts: Toast[];
  onRemove: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 items-end pointer-events-none"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
}
